import { ReactElement, ReactNode } from 'react';

import { CSSProperties } from 'styled-components';

import { Button } from '@components/button';
import { ButtonVariant } from '@components/button/button';
import { IconProps } from '@components/icons';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type SegmentConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  text?: string;
  textColor?: Color;
  onClick?: () => void;
  menuActions?: ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  style?: CSSProperties;
  textStyles?: CSSProperties;
};

export type MultiSegmentedFiltersProps = {
  segments: SegmentConfig[];
  onSegmentClick?: (segmentId: string) => void;
  dataTestid?: string;
};

export const MultiSegmentedFilters = ({ segments, dataTestid }: MultiSegmentedFiltersProps) => {
  // Find the segment with menuActions to apply the testId
  // Prefer 'selected-value' if it exists, otherwise use the first segment with menuActions
  const selectedValueIndex = segments.findIndex(
    (segment) => segment.id === 'selected-value' && segment.menuActions
  );
  const segmentWithMenuIndex =
    selectedValueIndex !== -1
      ? selectedValueIndex
      : segments.findIndex((segment) => segment.menuActions);

  return (
    <Stack orientation="horizontal" align="center" style={{ gap: pxToRem(1) }}>
      {segments.map((segment, index) => {
        const isFirst = index === 0;
        const isLast = index === segments.length - 1;

        const buttonStyles: CSSProperties = {
          borderRadius: isFirst
            ? `${pxToRem(8)} 0 0 ${pxToRem(8)}`
            : isLast
            ? `0 ${pxToRem(8)} ${pxToRem(8)} 0`
            : '0',
          minHeight: pxToRem(32),
          maxHeight: pxToRem(32),
          padding: segment.icon && !segment.text ? `0 ${pxToRem(4)}` : `0 ${pxToRem(12)}`,
          backgroundColor: theme.color.uiTrans5,
          border: 'none',
          cursor: segment.menuActions || segment.onClick ? 'pointer' : 'default',
          ...(isFirst && { paddingLeft: pxToRem(4) }),
          ...segment.style,
        };

        const buttonProps = {
          variant: 'secondary' as ButtonVariant,
          text: segment.text,
          style: buttonStyles,
          color: segment.textColor,
          onClick: segment.onClick,
          textStyle: segment.textStyles,
          ...(segment.icon && { iconLeft: segment.icon }),
          ...(dataTestid && index === segmentWithMenuIndex && { dataTestid }),
        };

        if (!segment.menuActions) {
          return <Button {...buttonProps} />;
        }

        return (
          <Menu placement="bottom-start" trigger={Button} triggerProps={buttonProps}>
            {segment.menuActions}
          </Menu>
        );
      })}
    </Stack>
  );
};
